import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * SponsorBanner - Displays sponsor images at the bottom of public pages
 * Allows clubs to monetize their tournaments by selling sponsor slots
 */
export const SponsorBanner = ({ sponsorUrls, position = 'bottom' }) => {
    const { t } = useTranslation()

    if (!sponsorUrls || sponsorUrls.length === 0) return null

    // Parse URLs (can be comma-separated string or array)
    const urls = Array.isArray(sponsorUrls)
        ? sponsorUrls
        : sponsorUrls.split(',').map(url => url.trim()).filter(Boolean)

    if (urls.length === 0) return null

    const positionClasses = {
        top: 'top-0',
        bottom: 'bottom-0',
        left: 'left-0 top-1/2 -translate-y-1/2',
        right: 'right-0 top-1/2 -translate-y-1/2'
    }

    let isEsports = false
    try {
        const context = useOutletContext()
        if (context && context.isEsports) {
            isEsports = true
        }
    } catch (e) {
        // Fallback
    }

    return (
        <div className={`w-full py-4 px-6 border-t transition-all duration-300 ${
            isEsports 
                ? 'bg-gradient-to-r from-[#0b0b14]/90 via-[#121222]/90 to-[#0b0b14]/90 border-purple-500/20' 
                : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-200'
        } ${positionClasses[position] || ''}`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-3">
                    <p className={`text-xs uppercase tracking-wider font-semibold ${isEsports ? 'text-purple-400 font-mono' : 'text-gray-400'}`}>
                        {t('sponsor.presented_by', 'Presented by')}
                    </p>
                </div>

                {/* Sponsor Logos */}
                <div className={`flex items-center justify-center gap-6 flex-wrap ${urls.length === 1 ? 'justify-center' : 'justify-evenly'}`}>
                    {urls.map((url, index) => (
                        <div
                            key={index}
                            className="relative group animate-fade-in"
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <div className={`rounded-lg p-4 transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-purple-950/30 border border-purple-500/20 hover:border-cyan-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)]' 
                                    : 'bg-white shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200'
                            }`}>
                                <img
                                    src={url}
                                    alt={`Sponsor ${index + 1}`}
                                    className={`h-12 max-w-[180px] object-contain transition-all duration-300 ${
                                        isEsports 
                                            ? 'opacity-85 hover:opacity-100 brightness-110 contrast-110' 
                                            : 'grayscale hover:grayscale-0 opacity-70 hover:opacity-100'
                                    }`}
                                    onError={(e) => {
                                        e.target.style.display = 'none'
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Optional: Contact for sponsorship */}
                {urls.length < 3 && (
                    <div className="text-center mt-4">
                        <p className={`text-xs ${isEsports ? 'text-slate-400 font-mono' : 'text-gray-400'}`}>
                            {t('sponsor.slot_available', '💼 Sponsor slot available')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
